/**
 * P3-08: Gather / Refine 核心按钮（迁移自 BuildingsModern.addCoreBtns，
 * js/buildings.js:3500-3523）。
 *
 * - GatherCatnipButton：ButtonModern + GatherCatnipButtonController
 *   （buildings.js:2978-2996）。无 prices → 恒 enabled（hasRes([])）；点击
 *   buyItem → gatherClicks 连点作弊计数（2500 次/2.5s 窗口）+ bld.gatherCatnip()；
 * - RefineCatnipButton：RefineCatnipButton + RefineCatnipButtonController
 *   （buildings.js:2998-3056）。prices=[{catnip, advancedRefinement 已研究 ? 50 : 100}]
 *   （3510/3519）；handler → game.bld.refineCatnip()（3515-3517）；
 *   **x100Link**：fetchModel 产出 {title:"x100", visible: catnip>=100×cost,
 *   handler→handleX100Click}（3004-3038），旧 RefineCatnipButton.update 按
 *   visible 切 display（3044-3054）——此处经 GameButton links 的 visible 语义
 *   等价表达（core.js:1625）。
 *
 * tooltip：两个按钮都挂 ButtonModernHelper.getTooltipHTML（core.js:1414-1497；
 * gather 仅 desc 块，refine 另有 simplePrices 价格块）。旧版定位偏移按钮右侧
 * 300px（core.js:1605）不复刻，遵循 TooltipProvider 锚点下方约定（GameButton
 * 文件头已知差异声明）。
 *
 * 渲染位置：仅筛选组（all/available/allEnabled/togglable/iw）下出现
 * （renderActiveGroup 的 i==0 分支，buildings.js:3418-3431；INVENTORY Part 1），
 * 由 P3-10 的 BonfireTab 组合；本文件不注册挂载入口。
 *
 * controller 创建：useMemo（gamePage 就绪后稳定；本组件经 MidPanel 在
 * initGame 后挂载，见 useButtonModel 文件头前提）。
 */
import { useCallback, useMemo } from "react";
import type { MouseEvent } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { t } from "../../bridge/i18n";
import { GameButton } from "../../components/GameButton";
import type { GameButtonLinkModel } from "../../components/GameButton";
import { makeModernTooltip, shallowSnapEq, useButtonModel } from "./useButtonModel";

interface CoreBtnSnapshot {
  name: string;
  description: string;
  visible: boolean;
  enabled: boolean;
  resourceIsLimited: boolean | string;
  twoRow: boolean;
}

interface RefineSnapshot extends CoreBtnSnapshot {
  /** model.x100Link.visible（buildings.js:3006） */
  x100Visible: boolean;
}

function toCoreSnapshot(model: Record<string, unknown>): CoreBtnSnapshot {
  return {
    name: model.name as string,
    description: (model.description as string) ?? "",
    visible: model.visible !== false,
    enabled: model.enabled !== false,
    resourceIsLimited: (model.resourceIsLimited as boolean | string) ?? "",
    twoRow: !!model.twoRow,
  };
}

// ---------------------------------------------------------------------------
// Gather Catnip（buildings.js:3502-3508）
// ---------------------------------------------------------------------------

export function GatherCatnipButton({ twoRow = false }: { twoRow?: boolean }) {
  const controller = useMemo(() => {
    const game = getGame();
    const Ctor = window.classes?.game?.ui?.GatherCatnipButtonController;
    if (!game || !Ctor) {
      return null;
    }
    return new Ctor(game);
  }, []);
  const createOpts = useCallback(
    () => ({
      name: t("buildings.gatherCatnip.label"),
      description: t("buildings.gatherCatnip.desc"),
      twoRow,
    }),
    [twoRow],
  );
  const { snap, modelRef } = useButtonModel(
    controller,
    createOpts,
    toCoreSnapshot,
    shallowSnapEq,
  );

  const onClick = useCallback(
    (event: MouseEvent) => {
      // 旧 Button.onClick（core.js:1021-1028）：animate 由 GameButton 承担，
      // 此处直调 buyItem；引擎自守（Gather 恒 itemBought）
      controller?.buyItem?.(modelRef.current, event);
    },
    [controller, modelRef],
  );

  const getTooltip = useMemo(
    () => makeModernTooltip(() => controller, createOpts),
    [controller, createOpts],
  );

  if (!snap) {
    return null;
  }
  return (
    <GameButton
      name={snap.name}
      description={snap.description}
      visible={snap.visible}
      enabled={snap.enabled}
      resourceIsLimited={snap.resourceIsLimited}
      twoRow={snap.twoRow}
      getTooltip={getTooltip}
      onClick={onClick}
    />
  );
}

// ---------------------------------------------------------------------------
// Refine Catnip（buildings.js:3510-3522 + 2998-3056）
// ---------------------------------------------------------------------------

function toRefineSnapshot(model: Record<string, unknown>): RefineSnapshot {
  const x100Link = model.x100Link as { visible?: boolean } | undefined;
  return {
    ...toCoreSnapshot(model),
    x100Visible: !!x100Link?.visible,
  };
}

export function RefineCatnipButton({ twoRow = false }: { twoRow?: boolean }) {
  const controller = useMemo(() => {
    const game = getGame();
    const Ctor = window.classes?.game?.ui?.RefineCatnipButtonController;
    if (!game || !Ctor) {
      return null;
    }
    return new Ctor(game);
  }, []);
  const createOpts = useCallback(
    (game: GamePage) => {
      // buildings.js:3510/3519：advancedRefinement 已研究 → 50，否则 100
      const isEnriched = !!(
        game.workshop as { get(name: string): { researched: boolean } }
      ).get("advancedRefinement").researched;
      return {
        name: t("buildings.refineCatnip.label"),
        description: t("buildings.refineCatnip.desc"),
        handler: () => {
          // buildings.js:3515-3517（旧 handler(btn) 签名忽略参数）
          (game.bld as { refineCatnip(): void }).refineCatnip();
        },
        prices: [{ name: "catnip", val: isEnriched ? 50 : 100 }],
        twoRow,
      };
    },
    [twoRow],
  );
  const { snap, modelRef } = useButtonModel(
    controller,
    createOpts,
    toRefineSnapshot,
    shallowSnapEq,
  );

  const onClick = useCallback(
    (event: MouseEvent) => {
      controller?.buyItem?.(modelRef.current, event);
    },
    [controller, modelRef],
  );

  const getTooltip = useMemo(
    () => makeModernTooltip(() => controller, createOpts),
    [controller, createOpts],
  );

  if (!snap) {
    return null;
  }

  // buildings.js:3004-3010 + RefineCatnipButton.update 3044-3054：
  // x100 链接 display 随 model.x100Link.visible 切换；handler 取最新 model
  // 的闭包（INVENTORY 风险点 6，与旧 this.model 语义一致）
  const links: GameButtonLinkModel[] = [
    {
      title: "x100",
      visible: snap.x100Visible,
      handler: () => {
        const model = modelRef.current as
          | { x100Link?: { handler: () => void } }
          | null;
        model?.x100Link?.handler();
      },
    },
  ];

  return (
    <GameButton
      name={snap.name}
      description={snap.description}
      visible={snap.visible}
      enabled={snap.enabled}
      resourceIsLimited={snap.resourceIsLimited}
      twoRow={snap.twoRow}
      links={links}
      getTooltip={getTooltip}
      onClick={onClick}
    />
  );
}
